// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { Store } from 'antd/lib/form/interface';
import { Form, FormInstance } from 'antd';
import { CounterWidgetType, WidgetOutput, ObjectTypeV1 } from 'thehive-sdk';
import { SeriesField } from '@Organisms/Dashboard/WidgetManage/SeriesField';
import DividerBasics from '@Organisms/Dashboard/WidgetManage/WidgetFormCommon/DividerBasics';
import DividerFilter from '@Organisms/Dashboard/WidgetManage/WidgetFormCommon/DividerFilter';
import ProvidedWidgetFormFilter from '@Organisms/Dashboard/WidgetManage/WidgetFormFilter/ProvidedWidgetFormFilter';
import TitleField from '@Organisms/Dashboard/WidgetManage/WidgetFormField/TitleField';
import EntityField from '@Organisms/Dashboard/WidgetManage/WidgetFormField/EntityField';
import { useLocalMessage } from '@Helpers/locale';
import { THForm } from '@Molecules/THForm/THForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { entitiesToExclude } from '@Constants/dashboard';

export interface CounterValues extends Store {
    title: string;
    entity: ObjectTypeV1;
    series: any[];
    periodDateField?: string;
}

interface CounterFormProps {
    onFinish: (values: CounterValues) => void;
    defaultValues?: WidgetOutput & { options: CounterWidgetType };
    form: FormInstance;
    onCancel: () => void;
    onChange: () => void;
    isvalid?: boolean;
}

export const CounterForm = ({
    onFinish,
    defaultValues,
    form,
    onChange,
    onCancel,
}: CounterFormProps): ReactElement | null => {
    const i = useLocalMessage();

    const entity = Form.useWatch(['entity'], form);

    return (
        <THForm<CounterValues>
            name="counter-form"
            form={form}
            onFinish={onFinish}
            onValuesChange={onChange}
            initialValues={{
                title: defaultValues?.options.title,
                interval: defaultValues?.options.interval,
                entity: defaultValues?.options.entity,
                series: defaultValues?.options.series,
                filters: defaultValues?.options.filters,
                periodDateField: defaultValues?.options.periodDateField || '_createdAt',
            }}
        >
            <THForm.Layout
                footer={<FormSubmitButton onCancel={onCancel} disabled={false} footerText={i('general.confirm')} />}
            >
                <DividerBasics />
                <TitleField />
                <EntityField
                    form={form}
                    showPeriodDate
                    entitiesToExclude={[
                        ...entitiesToExclude,
                        ObjectTypeV1.ACTION,
                        ObjectTypeV1.COMMENT,
                        ObjectTypeV1.CUSTOM_EVENT,
                        ObjectTypeV1.SHARE,
                        ObjectTypeV1.TAXONOMY,
                    ]}
                />
                <DividerFilter />

                <THForm.Item name="filters">
                    <ProvidedWidgetFormFilter entity={entity} />
                </THForm.Item>

                {entity && <SeriesField form={form} counterField />}
            </THForm.Layout>
        </THForm>
    );
};
